// Taxonomia Sanofi (matriz de complexidade do cliente, 23/10): só visual, nada é gravado.
// 6 critérios de 1 a 3 pontos; a soma classifica o evento em Simples (6–9), Média (10–14)
// ou Complexa (15–18). Lê o evento (convidados esperados, realização) e os itens do orçamento,
// por isso a tag muda enquanto a operadora monta o orçamento.
// Decisões do Rafael (23/10): sessões = quantidade de salas (sem olhar se são simultâneas);
// viagens = passageiros de aéreo (> 50 = complexa, sem VIP/país de risco); Evento Básico fica
// para quando a Mari trouxer os tipos de evento.
// Itens casam pelo código do catálogo 2026 com fallback pelo nome (grupo homônimo mantém o código antigo).

import { normalizarConvidadosEsperados } from './convidadosEsperadosPolicy';

export const CLASSIFICACOES_TAXONOMIA_SANOFI = [
    { chave: 'simples', rotulo: 'Simples', minimo: 6, maximo: 9 },
    { chave: 'media', rotulo: 'Média', minimo: 10, maximo: 14 },
    { chave: 'complexa', rotulo: 'Complexa', minimo: 15, maximo: 18 },
];

const GRUPO = {
    hospedagem: 'HOSPEDAGEM',
    aereo: 'AEREO',
    salas: 'ESPACOS/SALAS',
    ativacoes: 'ATIVACOES',
    equipamentos: 'EQUIPAMENTOS',
    internet: 'INTERNET',
    profissionaisPlenaria: 'PROFISSIONAIS PLENARIA',
};
const CODIGO_GRUPO = {
    hospedagem: '3001', aereo: '3002', salas: '3004', equipamentos: '3007',
    internet: '3013', profissionaisPlenaria: '3022', ativacoes: '3024',
};

const CODIGO_SALA = { 3066: 1, 3067: 1, 3068: 2, 3069: 2, 3070: 3, 3071: 3, 3072: 4, 3073: 4 };
const TAXA_INSCRICAO = ['3048'];
const GESTAO_PARTICIPANTES = { codigos: ['3397', '3398', '3399', '3402'], nomes: ['RSVP', 'INCLUSAO DE PARTICIPANTE', 'GERENCIAMENTO PARTICIPANTE', 'PLANTAO RSVP'] };
const TRANSMISSAO = { codigos: ['3150', '3151', '3240', '3241'], nomes: ['TRANSMISSAO DIGITAL/ONLINE', 'TRANSMISSAO SIMULTANEA', 'SALA STREAMING', 'TELA TRANSMISSAO'] };
const PRODUCAO = { codigos: ['3184', '3185', '3357', '3358', '3359'], nomes: ['PALCO', 'PLENARIA', 'DIRECAO HOUSE', 'ASSISTENTE DIRECAO HOUSE', 'SONOPLASTIA'] };
const TECNICO_AVULSO = { codigos: ['3188', '3153', '3273'], nomes: ['TELA', 'FILMAGEM + GRAVACAO DE CONTEUDO', 'FILMAGEM'] };

const normalizar = (texto) => String(texto ?? '')
    .normalize('NFD').replace(/[̀-ͯ]/g, '').trim().toUpperCase()
    .replace(/^\d+\s*-\s*/, '').replace(/\s+/g, ' ');
const codigoDe = (produto) => String(produto?.codigo ?? '').trim();
const nomeDe = (produto) => normalizar(produto?.nome ?? produto?.label);

const ehGrupo = (grupo, chave) => codigoDe(grupo?.produto) === CODIGO_GRUPO[chave] || nomeDe(grupo?.produto) === GRUPO[chave];
const casa = (produto, lista) => lista.codigos.includes(codigoDe(produto)) || lista.nomes.includes(nomeDe(produto));

const numero = (valor) => {
    if (typeof valor === 'number') return Number.isFinite(valor) ? valor : 0;
    const texto = String(valor ?? '').trim();
    if (!texto) return 0;
    const convertido = Number(texto.includes(',') ? texto.replace(/\./g, '').replace(',', '.') : texto);
    return Number.isFinite(convertido) ? convertido : 0;
};
// Última coluna preenchida da linha: Fechamento, senão Negociado, senão Pré-Evento.
const quantidadeVigente = (linha) => ['Fechamento', 'Negociado', 'PreEvento']
    .map((etapa) => numero(linha?.[`quantidade${etapa}`]))
    .find((quantidade) => quantidade > 0) || 0;

// Imposto e Outras Taxas existem em quase todo grupo: sozinhas não são o serviço do grupo.
const ehLinhaAuxiliar = (produto) => /^(IMPOSTO|OUTRAS TAXAS)\b/.test(nomeDe(produto));

// Só linhas com item escolhido (sem as auxiliares); cada uma leva junto o grupo dela.
function linhasComProduto(grupos) {
    return (Array.isArray(grupos) ? grupos : []).flatMap((grupo) => (Array.isArray(grupo?.subitem) ? grupo.subitem : [])
        .filter((linha) => linha?.produto && (codigoDe(linha.produto) || nomeDe(linha.produto)) && !ehLinhaAuxiliar(linha.produto))
        .map((linha) => ({ grupo, linha })));
}

function numeroDaSala(produto) {
    const porCodigo = CODIGO_SALA[codigoDe(produto)];
    if (porCodigo) return porCodigo;
    const porNome = nomeDe(produto).match(/^SALA (\d+)\b/);
    return porNome ? Number(porNome[1]) : null;
}

function criterioParticipantes(convidadosEsperados) {
    const pax = normalizarConvidadosEsperados(convidadosEsperados);
    if (pax === null || pax <= 0) return { pontos: null, motivo: 'Informe os convidados esperados no evento' };
    const pontos = pax <= 49 ? 1 : pax <= 100 ? 2 : 3;
    return { pontos, motivo: `${pax} convidados esperados` };
}

function criterioFormato(tipoRealizacao) {
    const formatos = { digital: [1, 'Digital'], presencial: [2, 'Presencial'], hibrido: [3, 'Híbrido'] };
    const formato = formatos[String(tipoRealizacao ?? '').trim().toLowerCase()];
    if (!formato) return { pontos: null, motivo: 'Informe a realização no evento' };
    return { pontos: formato[0], motivo: formato[1] };
}

function criterioSessoes(linhas) {
    const salas = new Set(linhas
        .filter(({ grupo, linha }) => ehGrupo(grupo, 'salas') || CODIGO_SALA[codigoDe(linha.produto)])
        .map(({ linha }) => numeroDaSala(linha.produto))
        .filter((sala) => sala !== null));
    const quantidade = salas.size;
    const pontos = quantidade <= 1 ? 1 : quantidade === 2 ? 2 : 3;
    return { pontos, motivo: quantidade === 0 ? 'Nenhuma sala no orçamento' : `${quantidade} sala${quantidade > 1 ? 's' : ''} no orçamento` };
}

function criterioLogistica(linhas) {
    const hospedagem = linhas.some(({ grupo, linha }) => ehGrupo(grupo, 'hospedagem') && !TAXA_INSCRICAO.includes(codigoDe(linha.produto)));
    const gestao = linhas.some(({ linha }) => casa(linha.produto, GESTAO_PARTICIPANTES));
    const atividades = linhas.some(({ grupo }) => ehGrupo(grupo, 'ativacoes'));
    const presentes = [hospedagem && 'hospedagem', gestao && 'gestão de participantes', atividades && 'ativações'].filter(Boolean);
    const pontos = hospedagem && gestao && atividades ? 3 : hospedagem && gestao ? 2 : 1;
    return { pontos, motivo: presentes.length ? `Com ${presentes.join(', ')}` : 'Sem hospedagem nem gestão de participantes' };
}

function criterioViagens(linhas) {
    const aereo = linhas.filter(({ grupo }) => ehGrupo(grupo, 'aereo'));
    if (!aereo.length) return { pontos: 1, motivo: 'Sem aéreo' };
    const passageiros = aereo.reduce((soma, { linha }) => soma + quantidadeVigente(linha), 0);
    return { pontos: passageiros > 50 ? 3 : 2, motivo: `${passageiros} passageiro${passageiros === 1 ? '' : 's'} de aéreo` };
}

function criterioTecnico(linhas) {
    const transmissao = linhas.some(({ linha }) => casa(linha.produto, TRANSMISSAO));
    const producao = linhas.some(({ linha }) => casa(linha.produto, PRODUCAO));
    if (transmissao && producao) return { pontos: 3, motivo: 'Transmissão com produção (palco ou direção técnica)' };
    const algum = transmissao || producao
        || linhas.some(({ grupo, linha }) => ehGrupo(grupo, 'equipamentos') || ehGrupo(grupo, 'internet')
            || ehGrupo(grupo, 'profissionaisPlenaria') || casa(linha.produto, TECNICO_AVULSO));
    return algum ? { pontos: 2, motivo: 'Equipamentos, tela, gravação ou transmissão' } : { pontos: 1, motivo: 'Sem equipamento técnico' };
}

export function calcularTaxonomiaSanofi({ convidadosEsperados, tipoRealizacao, grupos } = {}) {
    const linhas = linhasComProduto(grupos);
    const criterios = [
        { chave: 'participantes', rotulo: 'Participantes', ...criterioParticipantes(convidadosEsperados) },
        { chave: 'sessoes', rotulo: 'Sessões', ...criterioSessoes(linhas) },
        { chave: 'formato', rotulo: 'Formato', ...criterioFormato(tipoRealizacao) },
        { chave: 'logistica', rotulo: 'Coordenação logística', ...criterioLogistica(linhas) },
        { chave: 'viagens', rotulo: 'Viagens', ...criterioViagens(linhas) },
        { chave: 'tecnico', rotulo: 'Requisitos técnicos', ...criterioTecnico(linhas) },
    ];
    const completo = criterios.every((criterio) => criterio.pontos !== null);
    const total = criterios.reduce((soma, criterio) => soma + (criterio.pontos || 0), 0);
    const classificacao = completo
        ? CLASSIFICACOES_TAXONOMIA_SANOFI.find((faixa) => total >= faixa.minimo && total <= faixa.maximo) ?? null
        : null;
    return { criterios, total, completo, classificacao: classificacao?.chave ?? null, rotulo: classificacao?.rotulo ?? null };
}
